import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Plus, Calendar, Users, Home, Clock, Building2, Trash2, CheckCircle, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateLocal } from '@/utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PER_PAGE = 10;

const Dashboard = () => {
  const navigate = useNavigate();
  const { token, agent } = useAuth();
  const [events, setEvents] = useState([]);
  const [assignedEventIds, setAssignedEventIds] = useState(new Set());
  const [brokerNameMap, setBrokerNameMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalEvents: 0, totalVisitors: 0, activeEvents: 0 });
  const [activeTab, setActiveTab] = useState('assigned');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { setPage(1); }, [activeTab, searchQuery]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [eventsRes, assignmentsRes] = await Promise.all([
        axios.get(`${API}/events`, { headers }),
        axios.get(`${API}/agent/assignments`, { headers }).catch(() => ({ data: [] }))
      ]);

      setEvents(eventsRes.data);

      const assignmentData = (assignmentsRes.data || []);
      const assignedIds = new Set(assignmentData.filter(a => a.event_id).map(a => a.event_id));
      setAssignedEventIds(assignedIds);

      const brokerMap = {};
      assignmentData.forEach(a => {
        if (a.event_id && a.broker_name) brokerMap[a.event_id] = a.broker_name;
      });
      setBrokerNameMap(brokerMap);
      
      const totalVisitors = eventsRes.data.reduce((sum, event) => sum + event.visitor_count, 0);
      const activeEvents = eventsRes.data.filter(e => e.status === 'active').length;
      setStats({ totalEvents: eventsRes.data.length, totalVisitors, activeEvents });
    } catch (error) {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const assignedEvents = events.filter(e => assignedEventIds.has(e.id) && e.status !== 'done');
  const myEvents = events.filter(e => !assignedEventIds.has(e.id) && e.status !== 'done');
  const doneEvents = events.filter(e => e.status === 'done').sort((a, b) => b.date.localeCompare(a.date));

  const currentList = activeTab === 'assigned' ? assignedEvents : activeTab === 'my' ? myEvents : doneEvents;

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return currentList;
    const q = searchQuery.toLowerCase();
    return currentList.filter(e => (e.property_address || '').toLowerCase().includes(q));
  }, [currentList, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const handleMarkDone = async (e, eventId) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/events/${eventId}/status`, { status: 'done' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Open house marked as done');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteEvent = async (e, eventId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this open house?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Open house deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete open house');
    }
  };

  if (agent?.role === 'broker' && agent?.status === 'pending') {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto mt-20">
          <Card className="p-8 text-center">
            <div className="mb-4"><Clock className="mx-auto text-yellow-600" size={64} /></div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] mb-4" style={{fontFamily: '"Playfair Display", serif'}}>Pending Admin Approval</h2>
            <p className="text-lg text-stone-600 mb-6">Your broker account is awaiting approval from the administrator. You'll receive access to all features once your account is approved.</p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800"><strong>What's next?</strong> Our admin team will review your account shortly. You'll be notified via email once approved.</p>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  if (agent?.role === 'agent' && agent?.status === 'pending') {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto mt-20">
          <Card className="p-8 text-center" data-testid="agent-pending-banner">
            <div className="mb-4"><Clock className="mx-auto text-yellow-600" size={64} /></div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] mb-4" style={{fontFamily: '"Playfair Display", serif'}}>Awaiting Broker Activation</h2>
            <p className="text-lg text-stone-600 mb-6">Your account is pending activation by your broker. Once activated, you'll be able to create open houses, manage properties, and access all agent features.</p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800"><strong>What's next?</strong> Please contact your broker to activate your account.</p>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  const renderEventCard = (event) => {
    const isAssigned = assignedEventIds.has(event.id);
    const isDone = event.status === 'done';

    return (
      <Card
        key={event.id}
        data-testid={`event-card-${event.id}`}
        onClick={() => navigate(`/events/${event.id}`)}
        className={`bg-white px-5 py-4 rounded-xl card-shadow hover:card-shadow-hover transition-all duration-200 cursor-pointer ${
          isDone ? 'border-l-4 border-l-stone-300' : isAssigned ? 'border-l-4 border-l-[#D4A373]' : ''
        }`}
      >
        <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap">
          <div className={`p-2 rounded-full flex-shrink-0 hidden sm:flex ${
            isDone ? 'bg-stone-100' : isAssigned ? 'bg-[#D4A373]/10' : 'bg-[#1A4D2E]/10'
          }`}>
            <Home className={isDone ? 'text-stone-500' : isAssigned ? 'text-[#D4A373]' : 'text-[#1A4D2E]'} size={20} strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-[#1A4D2E] truncate text-base" style={{fontFamily: '"Playfair Display", serif'}}>{event.property_address}</h3>
            <div className="flex items-center gap-3 text-xs text-stone-500 mt-1 flex-wrap">
              <span className="flex items-center gap-1"><Calendar size={13} />{formatDateLocal(event.date)}</span>
              <span className="flex items-center gap-1"><Clock size={13} />{event.time}</span>
              <span className="flex items-center gap-1"><Users size={13} />{event.visitor_count} visitors</span>
              {isAssigned && brokerNameMap[event.id] && <span className="flex items-center gap-1"><Building2 size={13} />{brokerNameMap[event.id]}</span>}
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
            isDone ? 'bg-stone-100 text-stone-600' : event.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'
          }`}>{isDone ? 'done' : event.status}</span>
          {!isDone && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button data-testid={`done-btn-${event.id}`} onClick={(e) => handleMarkDone(e, event.id)} className="flex items-center gap-1 text-xs font-semibold text-green-700 hover:text-green-800 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-full transition-colors">
                <CheckCircle size={14} /> Done
              </button>
              {!isAssigned && event.visitor_count === 0 && (
                <button data-testid={`delete-btn-${event.id}`} onClick={(e) => handleDeleteEvent(e, event.id)} className="flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full transition-colors">
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
          )}
        </div>
      </Card>
    );
  };

  const emptyLabel = activeTab === 'assigned'
    ? { icon: Calendar, title: 'No assigned open houses yet', sub: '' }
    : activeTab === 'my'
    ? { icon: Home, title: 'Add Properties to create your own open house', sub: '' }
    : { icon: CheckCircle, title: 'No completed open houses yet', sub: '' };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>Dashboard</h1>
            <p className="text-stone-600">Manage your open house events</p>
          </div>
          {agent?.role === 'broker' && (
            <Button
              data-testid="create-event-button"
              onClick={() => navigate('/events/create')}
              className="bg-[#1A4D2E] text-white rounded-full px-8 py-6 text-lg hover:bg-[#143D24] transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-green-900/20"
            >
              <Plus className="mr-2" size={20} />
              Create Open House
            </Button>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-white p-8 rounded-2xl card-shadow hover:-translate-y-1 transition-transform duration-300" data-testid="stat-total-events">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-full bg-[#1A4D2E]/10"><Home className="text-[#1A4D2E]" size={28} strokeWidth={1.5} /></div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-stone-500">Total Open Houses</p>
                <p className="text-2xl sm:text-4xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>{stats.totalEvents}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-white p-8 rounded-2xl card-shadow hover:-translate-y-1 transition-transform duration-300" data-testid="stat-total-visitors">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-full bg-[#D4A373]/10"><Users className="text-[#D4A373]" size={28} strokeWidth={1.5} /></div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-stone-500">Total Visitors</p>
                <p className="text-2xl sm:text-4xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>{stats.totalVisitors}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-white p-8 rounded-2xl card-shadow hover:-translate-y-1 transition-transform duration-300" data-testid="stat-active-events">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-full bg-green-100"><Calendar className="text-green-600" size={28} strokeWidth={1.5} /></div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-stone-500">Active Open Houses</p>
                <p className="text-2xl sm:text-4xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>{stats.activeEvents}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Search by address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="dashboard-search"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 sm:gap-3 mb-6">
          <button onClick={() => setActiveTab('assigned')} data-testid="tab-assigned-open-houses"
            className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${activeTab === 'assigned' ? 'bg-[#1A4D2E] text-white shadow-md' : 'bg-white text-stone-600 border border-stone-200 hover:border-[#1A4D2E] hover:text-[#1A4D2E]'}`}>
            <span className="sm:hidden">Assigned OH ({assignedEvents.length})</span>
            <span className="hidden sm:inline">Assigned Open Houses ({assignedEvents.length})</span>
          </button>
          <button onClick={() => setActiveTab('my')} data-testid="tab-my-open-houses"
            className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${activeTab === 'my' ? 'bg-[#1A4D2E] text-white shadow-md' : 'bg-white text-stone-600 border border-stone-200 hover:border-[#1A4D2E] hover:text-[#1A4D2E]'}`}>
            <span className="sm:hidden">My OH ({myEvents.length})</span>
            <span className="hidden sm:inline">My Open Houses ({myEvents.length})</span>
          </button>
          <button onClick={() => setActiveTab('done')} data-testid="tab-done-open-houses"
            className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${activeTab === 'done' ? 'bg-[#1A4D2E] text-white shadow-md' : 'bg-white text-stone-600 border border-stone-200 hover:border-[#1A4D2E] hover:text-[#1A4D2E]'}`}>
            <span className="sm:hidden">Done OH ({doneEvents.length})</span>
            <span className="hidden sm:inline">Done Open Houses ({doneEvents.length})</span>
          </button>
        </div>

        {/* Results info */}
        {!loading && filtered.length > 0 && (
          <p className="text-xs text-stone-500 mb-3" data-testid="dashboard-results-info">
            Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length} events
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        )}

        {/* Event List */}
        {loading ? (
          <p className="text-stone-500">Loading events...</p>
        ) : filtered.length === 0 ? (
          <Card className="bg-white p-12 rounded-2xl card-shadow text-center" data-testid={`no-${activeTab}-events`}>
            {searchQuery ? (
              <>
                <Search className="mx-auto mb-4 text-stone-300" size={48} />
                <p className="text-lg text-stone-600">No events match "{searchQuery}"</p>
                <p className="text-sm text-stone-500 mt-2">Try a different search term</p>
              </>
            ) : (
              <>
                {React.createElement(emptyLabel.icon, { className: "mx-auto mb-4 text-stone-300", size: 48, strokeWidth: 1.5 })}
                <p className="text-lg text-stone-600">{emptyLabel.title}</p>
              </>
            )}
          </Card>
        ) : (
          <>
            <div className="space-y-3" data-testid={`${activeTab}-events-list`}>
              {paginated.map(renderEventCard)}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6" data-testid="dashboard-pagination">
                <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={page === 1} className="rounded-full px-3">First</Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-full"><ChevronLeft size={16} /></Button>
                <span className="text-sm text-stone-600 px-2">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="rounded-full"><ChevronRight size={16} /></Button>
                <Button variant="outline" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages} className="rounded-full px-3">Last</Button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
